"use client";

import React from "react";
import Image from "next/image";
import { Check } from "lucide-react";
import { motion, Variants } from "framer-motion";
import type { ActivePlanPricing } from "@/lib/pricing/get-active-plan-pricing";


interface ComparisonRow {
  feature: string;
  superpower: string | React.ReactNode;
  others: string;
}

// Track 5.Y-cat-launch-prep: superpower (FeelGood) cost row reads from
// the active plan resolver. Others column stays hardcoded — competitor
// price anchor, not our data, intentionally not pulled from panel_catalog.
function buildComparisonData(pricing: ActivePlanPricing): ComparisonRow[] {
  return [
  {
    feature: "Lab tests annually",
    superpower: <Check className="w-5 h-5" />,
    others: "One time only",
  },
  {
    feature: "Personalized protocols",
    superpower: <Check className="w-5 h-5" />,
    others: "Generic and not personalized",
  },
  {
    feature: "Upload past results",
    superpower: <Check className="w-5 h-5" />,
    others: "No past result interpretation",
  },
  {
    feature: "Total heart health testing",
    superpower: <Check className="w-5 h-5" />,
    others: "Only checks cholesterol levels",
  },
  {
    feature: "Hormones and fertility",
    superpower: <Check className="w-5 h-5" />,
    others: "Never tested unless feeling symptoms",
  },
  { feature: "Cost", superpower: pricing.priceDisplay, others: "$300+ per visit, then more per test" },
  ];
}

const ComparisonTable = ({ pricing }: { pricing: ActivePlanPricing }) => {
  const comparisonData = buildComparisonData(pricing);
    // Add : Variants here
    const containerVariants: Variants = {
      hidden: { opacity: 0, y: 20, scale: 0.98 },
      visible: { 
        opacity: 1, 
        y: 0, 
        scale: 1,
        transition: { 
          duration: 0.6, 
          ease: "easeOut", 
          staggerChildren: 0.1 
        } 
      }
    };
  
    const rowVariants: Variants = {
      hidden: { opacity: 0, x: -10 },
      visible: { opacity: 1, x: 0, transition: { duration: 1 } }
    };
  
    return (
      <div className="relative min-h-screen w-full flex flex-col items-center justify-center py-12 p-4 text-white overflow-hidden">
        <div className="absolute inset-0">
          <Image
            src="/images/science-orange-bg.jpg"
            alt="Background"
            fill
            priority
            className="object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>
  
        <div className="relative max-w-6xl w-full space-y-12">
          <motion.div
            initial={{ filter: "blur(10px)", opacity: 0, y: -20 }}
            whileInView={{ filter: "blur(0px)", opacity: 1, y: 0 }}
            viewport={{ margin: "-100px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight max-w-xl leading-tight">
              Testing and care you can&apos;t get anywhere else
            </h2>
          </motion.div>
  
          {/* 1. Main Table Container Animation */}
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ margin: "-100px" }}
            className="relative overflow-x-auto rounded-3xl border border-white/10 bg-white/10 backdrop-blur-xl shadow-2xl"
          >
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="py-6 px-8 font-normal text-transparent">Feature</th>
                  <th className="py-6 px-8 text-center relative">
                    {/* 2. Pulsing Glow for the highlight header */}
                    <motion.div 
                      animate={{ opacity: [1, 0.4, 1] }}
                      transition={{ duration: 1, ease: "easeInOut" }}
                      className="absolute inset-0 bg-white/10 backdrop-blur-xl -z-10 rounded-t-2xl mx-1" 
                    />
                    <span className="text-xl font-semibold tracking-tight">feelgood</span>
                  </th>
                  <th className="py-6 px-8 text-center font-medium text-gray-400">Others</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {comparisonData.map((row, index) => (
                  /* 3. Staggered Row Animation */
                  <motion.tr 
                    key={index} 
                    variants={rowVariants}
                    className="group"
                  >
                    <td className="py-5 px-8 text-gray-200 font-medium min-w-[200px] lg:min-w-[500px]">
                      {row.feature}
                    </td>
  
                    <td className="py-5 px-8 text-center relative">
                      <div className="absolute inset-y-0 inset-x-1 bg-white/10 backdrop-blur-xl -z-10" />
                      <div className="flex justify-center items-center h-full">
                        {typeof row.superpower === "string" ? (
                          <span className="text-2xl font-semibold">{row.superpower}</span>
                        ) : (
                          <motion.div 
                            whileHover={{ scale: 1.2 }}
                            className="rounded-full border border-white/40 p-1"
                          >
                            {row.superpower}
                          </motion.div>
                        )}
                      </div>
                    </td>
  
                    <td className="py-5 px-8 text-center text-gray-400 font-medium">
                      {row.others}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </div>
    );
  };
  
  export default ComparisonTable;